/**
 * Codex resolution — maps any namespaced ID to its source across feeds.
 * `person:*` that matches a squad player → the live player page; everything
 * else → a codex entry. Redirects let renamed/merged IDs resolve forward.
 */
import type { CodexEntry, CodexFeed, Player, SquadFeed, YearView } from './types';

export type Resolved =
  | { kind: 'codex'; entry: CodexEntry }
  | { kind: 'player'; player: Player };

const normTrophy = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Resolve an honour string ("Premier League 2025/26") to a codex trophy entry,
 * matching on the entry's subtitle (which is exactly the "Competition YYYY/YY"
 * form). Returns the entry id, or undefined when there's no history to show
 * (e.g. a Community Shield with no entry) — the chip then stays plain.
 */
export function honourEntryId(honour: string, codex: CodexFeed): string | undefined {
  const h = normTrophy(honour);
  const hit = codex.entries.find(
    (e) => e.kind === 'trophy' && e.subtitle && (h === normTrophy(e.subtitle) || h.startsWith(normTrophy(e.subtitle))),
  );
  return hit?.id;
}

export function resolveEntry(id: string, codex: CodexFeed, squad: SquadFeed): Resolved | null {
  const target = codex.redirects?.[id] ?? id;
  const player = squad.players.find((p) => p.personId === target);
  if (player) return { kind: 'player', player };
  const entry = codex.entries.find((e) => e.id === target || e.aliases?.includes(target));
  if (entry) return { kind: 'codex', entry };
  return null;
}

/**
 * Fold every codex timeline into a single calendar year — a read-time view, no
 * stored payload, no fabrication. Each highlight is a genuine timeline event
 * whose `year` matches, carrying the id + title of the entry it came from so
 * the UI can open the source. `relatedEntryIds` are the unique contributors,
 * plus any entry whose (optional, informal) `season` string names the year.
 * A year nothing folds to is `quiet` with an empty body — the calm stub.
 */
export function buildYearView(year: string, codex: CodexFeed): YearView {
  const highlights: YearView['highlights'] = [];
  const related = new Set<string>();

  for (const entry of codex.entries) {
    for (const t of entry.timeline) {
      if (t.year === year) {
        highlights.push({ event: t.event, entryId: entry.id, entryTitle: entry.title });
        related.add(entry.id);
      }
    }
    // Optional informal `season` string on an entry (e.g. "2003/04") — fold the
    // entry in as related when it names the year, even without a dated event.
    const season = (entry as { season?: unknown }).season;
    if (typeof season === 'string' && season.includes(year)) related.add(entry.id);
  }

  // No dated moment folds to this year. If an entry's season names it we still
  // surface those as related (a non-quiet stub with doorways but no events);
  // only a year that touches nothing at all is the truly quiet, empty stub.
  if (highlights.length === 0) {
    if (related.size === 0) return { year, summary: '', highlights: [], relatedEntryIds: [], quiet: true };
    return { year, summary: `${related.size} entr${related.size === 1 ? 'y' : 'ies'} touch ${year}.`, highlights: [], relatedEntryIds: [...related], quiet: false };
  }

  const summary =
    highlights.length === 1 ? highlights[0]!.event : `${highlights.length} moments from ${year}.`;

  return {
    year,
    summary,
    highlights,
    relatedEntryIds: [...related],
    quiet: false,
  };
}
